import { apiRequest } from "@/lib/api-client-v2";

export type TacticalBoard = {
  id: number;
  operation_id: number;
  name: string;
  description?: string | null;
  kind: string;
  organization_id?: number | null;
  center_lng?: number | null;
  center_lat?: number | null;
  zoom?: number | null;
};

export type TacticalLayerGroup = {
  id: number;
  board_id: number;
  name: string;
  display_order: number;
  is_locked: boolean;
  is_visible: boolean;
};

export type TacticalFeatureProps = {
  id: number;
  kind?: "point" | "line" | "polygon" | "text";
  source?: string;
  legendKey: string;
  name: string;
  iconUrl: string;
  sizePx: number;
  rotationDeg: number;
  strokeColor?: string;
  strokeWidth?: number;
  strokeDash?: "solid" | "dash" | "dot" | "dash_dot";
  fillColor?: string;
  fillOpacity?: number;
  label: string;
  objectId?: number | null;
  layerGroupId?: number | null;
  symbolKlsId?: number | null;
  symbolSidc?: string;
  originFeatureId?: string | number | null;
  compareRole?: "only_base" | "only_other" | "changed" | "unchanged";
  isLocked?: boolean;
  groupLocked?: boolean;
  effectivelyLocked?: boolean;
  isVisible?: boolean;
  groupVisible?: boolean;
  effectivelyVisible?: boolean;
  revision?: number;
};

export type TacticalSymbolMeta = {
  kls_id: number;
  code: string;
  label: string;
  sidc: string;
};

export type TacticalHistoryEvent = {
  id: number;
  board_id: number;
  feature_id: number;
  action: "create" | "update" | "delete";
  revision?: number | null;
  changed_by?: string | null;
  changed_at: string;
};

export type TacticalHistoryTimeline = {
  items: TacticalHistoryEvent[];
  total: number;
};

export type TacticalBoardCompare = {
  base_board: TacticalBoard;
  other_board: TacticalBoard;
  only_base: TacticalFeature[];
  only_other: TacticalFeature[];
  changed: Array<{ base: TacticalFeature; other: TacticalFeature; fields: string[] }>;
  unchanged_count: number;
};

export type TacticalBoardMemberRole = "viewer" | "editor" | "owner";

export type TacticalBoardMember = {
  id: number;
  board_id: number;
  user_id: string;
  role: TacticalBoardMemberRole;
};

export type TacticalTrack = {
  id: number;
  board_id: number;
  name: string;
  source: string;
  color: string;
  is_visible: boolean;
  point_count: number;
  last_observed_at?: string | null;
  geometry?: GeoJSON.LineString | GeoJSON.Point | null;
  head?: GeoJSON.Point | null;
};

export type TacticalFeatureCardField = {
  field_key: string;
  label: string;
  field_type: string;
  is_required: boolean;
  is_readonly: boolean;
  is_visible?: boolean;
  is_effectively_required?: boolean;
  is_effectively_readonly?: boolean;
  visibility_rules?: Record<string, unknown>;
  value?: unknown;
};

export type TacticalFeatureCardSection = {
  code: string;
  name: string;
  fields: TacticalFeatureCardField[];
};

export type TacticalFeatureCard = {
  feature_id: number;
  board_id: number;
  template_id?: number | null;
  template_code?: string | null;
  template_name?: string | null;
  sections: TacticalFeatureCardSection[];
  values: Record<string, unknown>;
  message?: string | null;
};

export type TacticalFeature = GeoJSON.Feature<
  GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon,
  TacticalFeatureProps
>;

const base = "/tactical";

export const tacticalApi = {
  listSymbols: () => apiRequest<TacticalSymbolMeta[]>(`${base}/meta/symbols`),
  listBoards: () => apiRequest<TacticalBoard[]>(`${base}/boards`),
  createBoard: (name: string, kind: string = "situation") =>
    apiRequest<TacticalBoard>(`${base}/boards`, {
      method: "POST",
      data: { name, kind },
    }),
  compareBoards: (baseId: number, otherId: number) =>
    apiRequest<TacticalBoardCompare>(
      `${base}/boards/compare?base_id=${baseId}&other_id=${otherId}`,
    ),
  getBoard: (id: number) => apiRequest<TacticalBoard>(`${base}/boards/${id}`),
  updateBoard: (id: number, data: Partial<TacticalBoard>) =>
    apiRequest<TacticalBoard>(`${base}/boards/${id}`, { method: "PATCH", data }),
  deleteBoard: (id: number) =>
    apiRequest<void>(`${base}/boards/${id}`, { method: "DELETE" }),

  listMembers: (boardId: number) =>
    apiRequest<TacticalBoardMember[]>(`${base}/boards/${boardId}/members`),
  addMember: (
    boardId: number,
    data: { user_id: string; role?: TacticalBoardMemberRole },
  ) =>
    apiRequest<TacticalBoardMember>(`${base}/boards/${boardId}/members`, {
      method: "POST",
      data,
    }),
  updateMember: (
    boardId: number,
    memberUserId: string,
    data: { role: TacticalBoardMemberRole },
  ) =>
    apiRequest<TacticalBoardMember>(
      `${base}/boards/${boardId}/members/${encodeURIComponent(memberUserId)}`,
      { method: "PATCH", data },
    ),
  removeMember: (boardId: number, memberUserId: string) =>
    apiRequest<void>(
      `${base}/boards/${boardId}/members/${encodeURIComponent(memberUserId)}`,
      { method: "DELETE" },
    ),

  listTracks: (boardId: number) =>
    apiRequest<TacticalTrack[]>(`${base}/boards/${boardId}/tracks`),
  createDemoTrack: (boardId: number) =>
    apiRequest<TacticalTrack>(`${base}/boards/${boardId}/tracks/demo`, {
      method: "POST",
    }),
  updateTrack: (
    boardId: number,
    trackId: number,
    data: Partial<Pick<TacticalTrack, "name" | "color" | "is_visible">>,
  ) =>
    apiRequest<TacticalTrack>(`${base}/boards/${boardId}/tracks/${trackId}`, {
      method: "PATCH",
      data,
    }),
  deleteTrack: (boardId: number, trackId: number) =>
    apiRequest<void>(`${base}/boards/${boardId}/tracks/${trackId}`, {
      method: "DELETE",
    }),

  listLayerGroups: (boardId: number) =>
    apiRequest<TacticalLayerGroup[]>(`${base}/boards/${boardId}/layer-groups`),
  createLayerGroup: (
    boardId: number,
    data: { name: string; display_order?: number; is_locked?: boolean; is_visible?: boolean },
  ) =>
    apiRequest<TacticalLayerGroup>(`${base}/boards/${boardId}/layer-groups`, {
      method: "POST",
      data,
    }),
  updateLayerGroup: (
    boardId: number,
    groupId: number,
    data: Partial<Pick<TacticalLayerGroup, "name" | "display_order" | "is_locked" | "is_visible">>,
  ) =>
    apiRequest<TacticalLayerGroup>(`${base}/boards/${boardId}/layer-groups/${groupId}`, {
      method: "PATCH",
      data,
    }),
  deleteLayerGroup: (boardId: number, groupId: number) =>
    apiRequest<void>(`${base}/boards/${boardId}/layer-groups/${groupId}`, {
      method: "DELETE",
    }),

  listFeatures: (boardId: number) =>
    apiRequest<{ type: "FeatureCollection"; features: TacticalFeature[] }>(
      `${base}/boards/${boardId}/features`,
    ),
  listFeaturesAsOf: (boardId: number, asOfIso: string) =>
    apiRequest<{ type: "FeatureCollection"; features: TacticalFeature[] }>(
      `${base}/boards/${boardId}/features/as-of?as_of=${encodeURIComponent(asOfIso)}`,
    ),
  listHistory: (boardId: number, limit = 500) =>
    apiRequest<TacticalHistoryTimeline>(
      `${base}/boards/${boardId}/history?limit=${limit}`,
    ),
  createFeature: (boardId: number, data: Record<string, unknown>) =>
    apiRequest<TacticalFeature>(`${base}/boards/${boardId}/features`, {
      method: "POST",
      data,
    }),
  mergeFeatures: (boardId: number, featureIds: number[]) =>
    apiRequest<TacticalFeature>(`${base}/boards/${boardId}/features/merge`, {
      method: "POST",
      data: { feature_ids: featureIds },
    }),
  differenceFeatures: (boardId: number, featureIds: number[]) =>
    apiRequest<TacticalFeature>(`${base}/boards/${boardId}/features/difference`, {
      method: "POST",
      data: { feature_ids: featureIds },
    }),
  splitFeature: (boardId: number, featureId: number, coordinates: number[][]) =>
    apiRequest<{ type: "FeatureCollection"; features: TacticalFeature[] }>(
      `${base}/boards/${boardId}/features/${featureId}/split`,
      {
        method: "POST",
        data: { coordinates },
      },
    ),
  updateFeature: (boardId: number, featureId: number, data: Record<string, unknown>) =>
    apiRequest<TacticalFeature>(`${base}/boards/${boardId}/features/${featureId}`, {
      method: "PATCH",
      data,
    }),
  deleteFeature: (boardId: number, featureId: number) =>
    apiRequest<void>(`${base}/boards/${boardId}/features/${featureId}`, {
      method: "DELETE",
    }),
  getFeatureCard: (boardId: number, featureId: number) =>
    apiRequest<TacticalFeatureCard>(`${base}/boards/${boardId}/features/${featureId}/card`),
  putFeatureCard: (boardId: number, featureId: number, values: Record<string, unknown>) =>
    apiRequest<TacticalFeatureCard>(`${base}/boards/${boardId}/features/${featureId}/card`, {
      method: "PUT",
      data: { values },
    }),
};

/** URL WebSocket комнаты доски (JWT в query, как у чата). */
export function tacticalWsUrl(token: string, boardId: number): string {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}/ws/tactical?token=${encodeURIComponent(token)}&board_id=${boardId}`;
}
